import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Loading } from 'src/components/Loading';
import ChartPreview from '../../ChartPreview';
import { Modal } from '../../Modal';
import { Selector } from '../../Selector';
import ChartExporter from './chartExporter/exporter';
import * as Ui from './styles';

/**
 * Componente que exibe as anotacoes dos resultados
 * de um card
 */
const propTypes = {
  onExport: PropTypes.func,
  onCancel: PropTypes.func,
  title: PropTypes.string,
  placeholder: PropTypes.string,
};

const defaultProps = {
  title: 'Exportar...',
  placeholder: 'Exporte o gráfico no formato de sua preferência',
  onExport: null,
  onCancel: null,
};

function CardExporting({
  /**
   * tipos de exportação
   */
  types = [/* {label, value} */],
  /**
   * propaga o evento de seleção do tipo de exportação
   */
  onTypeChange,
  /**
   * restante
   */
  ...props
}) {
  // state from props
  const { 
    onCancel, 
    title, 
    theme, 
    questionLabels, 
    selectedChartType, 
    selectedCardType, 
    surveyId, 
    analyzeId, 
    customerId, 
    idPerguntaCruzada, 
    idAlternativaCruzada 
  } = props;
  
  const [okBtnDisabled, setOkBtnDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showChartPreview, setShowChartPreview] = useState(false);
  const [checkedPreviewOptions, setCheckedPreviewOptions] = useState({});
  const [exportingType, setExportingType] = useState('');
  const [shouldExport, setShouldExport] = useState(false);
  const [lastSelectedItemIndex, setLastSelectedItemIndex] = useState([]);

  const { running, error } = useSelector((state) => state.chartExporting.exportingConfig);

  // Loading effect: irá setar o estado do loading da requisição para xlsx e csv
  useEffect(() => {
    if (running && !isLoading) {
      setIsLoading(true);
    } else if (!running && isLoading) {
      setIsLoading(false);
    }
  }, [running]);

  // Loading de erro: mostra uma mensagem de erro caso alguma requisição retorne um erro.
  useEffect(() => {
    if (error) {
      toast.error(error.toString());
    }
  }, [error]);

  // Effect: Ao fazer uma requisição para fazer o download de um xlsx ou csv o ultimo item selecionado
  // pelo usuário no seletor era perdido. Neste useEffect, o ultimo estado de seleção é preservado.
  useEffect(() => {
    const selectionIndex = types.findIndex((item) => item.value === exportingType);

    if (selectionIndex !== -1 && lastSelectedItemIndex[0] !== selectionIndex) {
      setLastSelectedItemIndex([selectionIndex]);
    }
  }, [exportingType, types]);

  function onUpdateExportingAction(exportingState) {
    setShouldExport(exportingState);
  }

  function handleExport() {
    onUpdateExportingAction(!shouldExport);
  }

  function buildExportingOptions() {
    const exportingOptions = {
      exportingType,
      ...(Object.keys(checkedPreviewOptions).length && { rules: checkedPreviewOptions }),
    };

    return exportingOptions;
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }
  }

  function onChange(changes) {
    if (changes && changes.length === 0) return;

    if (okBtnDisabled) {
      setOkBtnDisabled(false);
    }

    const newSelection = changes[0];

    setExportingType(newSelection.value);

    if (newSelection.value === 'png') {
      setShowChartPreview(true);
    } else if (showChartPreview) {
      setShowChartPreview(false);
    }

    if (onTypeChange) {
      onTypeChange(newSelection.value)
    };

  }

  function onUpdateCheckedPreviewOptions(checkedOptions) {
    setCheckedPreviewOptions(checkedOptions);
  }

  // Componente de loading quando uma requisição estiver sendo processada
  function LoadingBody() {
    return (
      <Ui.LoadingWrapper>
        <Loading size={30} running={isLoading} />
        <Ui.Label>Preparando arquivo, aguarde um instante...</Ui.Label>
      </Ui.LoadingWrapper>
    )
  }

  return (
    <>
      <Ui.Container
        okBtnDisabled={okBtnDisabled}
        className="CardExporting-container"
        showChartPreview={showChartPreview}
      >
        <Modal
          isOpen
          title={title}
          okBtnTitle={'Exportar'}
          cancelBtnTitle={'Voltar'}
          onOk={handleExport}
          onCancel={handleCancel}
          iconComponent={<Ui.ChartExportIcon />}
          width="597px"
          showActions={!isLoading ? true : false}
        >
          <Ui.Wrapper>
            {
              !isLoading ? (
                <>
                  <Ui.Label>Tipo de arquivo*</Ui.Label>
                  <Ui.Action>
                    <Selector
                      placeholder="Selecione o tipo de aquivo que deseja exportar"
                      type="single"
                      data={types}
                      onChange={onChange}
                      initialSelectedIndexes={lastSelectedItemIndex}
                    />
                  </Ui.Action>
                </>
              ) : (
                <LoadingBody />
              )
            }
            { showChartPreview && (
              <ChartPreview
                theme={theme}
                onUpdateCheckedPreviewOptions={onUpdateCheckedPreviewOptions}
                selectedChartType={selectedChartType}
              />
            )}
          </Ui.Wrapper>
        </Modal>
      </Ui.Container>
      <ChartExporter
        shouldExport={shouldExport}
        // eslint-disable-next-line react/jsx-no-bind
        onUpdateExportingAction={onUpdateExportingAction}
        exportingOptions={buildExportingOptions()}
        questionLabels={questionLabels}
        chartType={selectedChartType}
        cardType={selectedCardType}
        surveyId={surveyId}
        analyzeId={analyzeId}
        customerId={customerId}
        idPerguntaCruzada={idPerguntaCruzada}
        idAlternativaCruzada={idAlternativaCruzada}
      />
    </>
  );
}

CardExporting.propTypes = propTypes;
CardExporting.defaultProps = defaultProps;

export default CardExporting;
